import React from 'react';
import { Redirect } from 'react-router-dom';
import { arrayOf, bool, func, object, oneOfType, string } from 'prop-types';

import { NotificationContainer, NotificationManager } from 'react-notifications';
import { CircularProgress, Box } from '@mui/material';
import CodeVerificationForm from '../../components/authentication/CodeVerificationForm';
import IdentifierForm from '../../components/authentication/IdentifierForm';
import IdentifierPasswordForm from '../../components/authentication/IdentifierPasswordForm';
import NewPasswordForm from '../../components/authentication/NewPasswordForm';
import TermsAndConditionsForm from '../../components/authentication/TermsAndConditionsForm';
import TemporaryPasswordForm from '../../components/authentication/TemporaryPasswordForm';
import LogoLink from '../LogoLink';
import { AUTH_ERROR_TYPES } from '../../constants/Values';

/** Class representing a SignIn react component */
class SignIn extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      identifier: '',
      password: '',
      passwordRepeat: '',
      temporaryPassword: '',
      verificationCode: '',
    };
  }

  componentDidUpdate() {
    const { showMessage, hideMessage, signedIn, history } = this.props;
    if (showMessage) {
      setTimeout(() => {
        hideMessage();
      }, 100);
    }
    if (signedIn) {
      history.push('/');
    }
  }

  onAcceptTermsAndConditions = () => {
    const { acceptTermsAndConditions, accessToken, version } = this.props;
    acceptTermsAndConditions(accessToken, version);
  };

  onConfirmUserSignUp = () => {
    const { confirmUserSignUp } = this.props;
    const { identifier, password, verificationCode } = this.state;
    confirmUserSignUp(identifier, password, verificationCode);
  };

  onSubmitTemporaryPassword = () => {
    const { submitTemporaryPassword, accessToken } = this.props;
    const { temporaryPassword } = this.state;
    submitTemporaryPassword(accessToken, temporaryPassword);
  };

  onVerifyUser = () => {
    const { verifyUser } = this.props;
    const { identifier } = this.state;
    verifyUser(identifier);
  };

  onVerifyUserSignUp = () => {
    const { verifyUserSignUp } = this.props;
    const { identifier, verificationCode } = this.state;
    verifyUserSignUp(identifier, verificationCode);
  };

  onUserSignIn = () => {
    const { userSignIn } = this.props;
    const { identifier, password } = this.state;
    userSignIn(identifier, password);
  };

  updateStateField = (field, value) => this.setState({ [field]: value });

  render() {
    const {
      identifier,
      password,
      passwordRepeat,
      temporaryPassword,
      verificationCode,
    } = this.state;

    const {
      alertMessage,
      enterTemporaryPassword,
      loader,
      registered,
      resetAuthErrorMessage: resetErrorMessage,
      showMessage,
      showTermsAndConditions,
      signedIn,
      signUpConfirmed,
      signUpVerified,
      termsAndConditionsText,
      verified,
      validationErrors,
      showAuthLoader,
      backToIdentifierForm,
    } = this.props;

    return (
      <Box className="app-login-container" sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <div className="app-login-main-content">
          <Box className="app-logo-content" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <LogoLink />
          </Box>

          <div className="app-login-content">
            {signedIn && <Redirect to="/" />}
            {!verified && (
              <IdentifierForm
                errorMessage={validationErrors[AUTH_ERROR_TYPES.IDENTIFIER]}
                identifier={identifier}
                onFieldChange={this.updateStateField}
                resetErrorMessage={resetErrorMessage}
                showAuthLoader={showAuthLoader}
                verifyUser={this.onVerifyUser}
              />
            )}
            {verified &&
              registered &&
              !showTermsAndConditions &&
              !enterTemporaryPassword && (
                <IdentifierPasswordForm
                  errorMessage={validationErrors[AUTH_ERROR_TYPES.IDENTIFIER_PASS]}
                  identifier={identifier}
                  handleBackClick={backToIdentifierForm}
                  onFieldChange={this.updateStateField}
                  password={password}
                  resetErrorMessage={resetErrorMessage}
                  showAuthLoader={showAuthLoader}
                  userSignIn={this.onUserSignIn}
                />
              )}
            {verified &&
              !registered &&
              !signUpVerified && (
                <CodeVerificationForm
                  errorMessage={validationErrors[AUTH_ERROR_TYPES.VERIFICATION_CODE]}
                  handleBackClick={backToIdentifierForm}
                  onFieldChange={this.updateStateField}
                  resetErrorMessage={resetErrorMessage}
                  showAuthLoader={showAuthLoader}
                  verificationCode={verificationCode}
                  verifyUserSignUp={this.onVerifyUserSignUp}
                />
              )}
            {verified &&
              !registered &&
              signUpVerified &&
              !signUpConfirmed && (
                <NewPasswordForm
                  confirmUserSignUp={this.onConfirmUserSignUp}
                  errorMessage={validationErrors[AUTH_ERROR_TYPES.NEW_PASS]}
                  handleBackClick={backToIdentifierForm}
                  onFieldChange={this.updateStateField}
                  password={password}
                  passwordRepeat={passwordRepeat}
                  resetErrorMessage={resetErrorMessage}
                  showAuthLoader={showAuthLoader}
                />
              )}
            {verified &&
              registered &&
              signUpVerified &&
              signUpConfirmed &&
              showTermsAndConditions && (
                <TermsAndConditionsForm
                  acceptTermsAndConditions={this.onAcceptTermsAndConditions}
                  text={termsAndConditionsText}
                  showAuthLoader={showAuthLoader}
                />
              )}
            {verified &&
              registered &&
              signUpVerified &&
              signUpConfirmed &&
              enterTemporaryPassword && (
                <TemporaryPasswordForm
                  errorMessage={validationErrors[AUTH_ERROR_TYPES.TEMPORARY_PASS]}
                  onFieldChange={this.updateStateField}
                  resetErrorMessage={resetErrorMessage}
                  showAuthLoader={showAuthLoader}
                  submitTemporaryPassword={this.onSubmitTemporaryPassword}
                  temporaryPassword={temporaryPassword}
                />
              )}
          </div>
        </div>
        {loader && (
          <div className="loader-view">
            <CircularProgress />
          </div>
        )}
        {showMessage && NotificationManager.error(alertMessage)}
        <NotificationContainer />
      </Box>
    );
  }
}

SignIn.propTypes = {
  acceptTermsAndConditions: func,
  accessToken: string,
  alertMessage: oneOfType([arrayOf(string), string]),
  backToIdentifierForm: func,
  confirmUserSignUp: func,
  enterTemporaryPassword: bool,
  hideMessage: func,
  history: object.isRequired,
  loader: bool,
  registered: bool,
  resetAuthErrorMessage: func,
  showAuthLoader: func,
  showMessage: bool,
  showTermsAndConditions: bool,
  signedIn: bool,
  signUpConfirmed: bool,
  signUpVerified: bool,
  submitTemporaryPassword: func,
  termsAndConditionsText: string,
  userSignIn: func,
  validationErrors: object,
  verified: bool,
  version: string,
  verifyUser: func,
  verifyUserSignUp: func,
};

SignIn.defaultProps = {
  acceptTermsAndConditions: () => {},
  accessToken: '',
  alertMessage: [''],
  backToIdentifierForm: () => {},
  confirmUserSignUp: () => {},
  enterTemporaryPassword: false,
  hideMessage: () => {},
  loader: false,
  registered: false,
  resetAuthErrorMessage: () => {},
  showAuthLoader: () => {},
  showMessage: false,
  showTermsAndConditions: false,
  signedIn: false,
  signUpConfirmed: false,
  signUpVerified: false,
  submitTemporaryPassword: () => {},
  termsAndConditionsText: '',
  userSignIn: () => {},
  validationErrors: {},
  verified: false,
  verifyUser: () => {},
  verifyUserSignUp: () => {},
  version: '',
};

export default SignIn;
